/**
 * Identidade desta instância do cliente — esta aba (no desktop/mobile, esta janela ou este app).
 *
 * Todo request leva o id no header {@link HEADER_INSTANCIA}; o servidor devolve-o em
 * `porInstanciaId` nos eventos de tempo real. É assim que a tela reconhece o próprio eco (que ela
 * já aplicou de forma otimista) sem descartar o que o MESMO usuário fez em outro lugar — um agente
 * agindo pelo MCP em nome dele, ou ele mesmo em outra aba. Antes o filtro comparava o usuário, e
 * essas mudanças nunca chegavam à tela.
 *
 * Uma constante por carregamento da página, e não em `sessionStorage`: duplicar a aba copia o
 * `sessionStorage`, e as duas abas passariam a ignorar os eventos uma da outra.
 *
 * Espelha `Rocky.Shared/Helpers/InstanciaCliente.cs`.
 */
export const HEADER_INSTANCIA = 'X-Rocky-Instance-Id';

// `crypto.randomUUID` só existe em contexto seguro (https ou localhost). Abrir o dev server pelo IP
// da rede (ex.: testar no celular) não é contexto seguro — sem o fallback, o app nem carregaria.
const gerarId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; // versão 4
  b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

export const INSTANCIA_ID = gerarId();

/** Header para requests feitos fora do `authService.authFetch`. */
export const headerInstancia = (): Record<string, string> => ({ [HEADER_INSTANCIA]: INSTANCIA_ID });

/**
 * O evento é eco de uma ação desta própria instância? Sem `porInstanciaId` (MCP, job, webhook,
 * request sem o header) nunca é — e aí a tela recarrega, que é o lado seguro.
 */
export const ehEcoDestaInstancia = (porInstanciaId: string | null | undefined): boolean =>
  !!porInstanciaId && porInstanciaId.toLowerCase() === INSTANCIA_ID;
